import { FC, PropsWithChildren } from 'react';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './AlgorithmTargets.translations.json';
import { Flex } from 'antd';
import styled from 'styled-components';
import { colorGray, fontWeightStrong } from '@atlas-frontend/design-tokens';
import { TextEllipsis } from '../../../common/components';
import { AlgorithmTargetFormDataType } from './schema/types';
import { AlgorithmCondition } from '../algorithmCondition/AlgorithmCondition';
import type { AlgorithmMappingQueryDtoType } from '../mappings/schema/types';
import { RequiredKeyDataType } from '../../schema';

const { addTranslations, useTranslation } = i18nNsFactory('algorithm-targets');
addTranslations(translations);

export interface AlgorithmTargetProps extends PropsWithChildren {
  target: AlgorithmTargetFormDataType;
  mappings: AlgorithmMappingQueryDtoType[];
  requiredKeysByKeys: Map<string, RequiredKeyDataType>;
}

export const AlgorithmTarget: FC<AlgorithmTargetProps> = (props) => {
  const { target, mappings, requiredKeysByKeys, children } = props;
  const { t } = useTranslation();

  return (
    <Flex vertical wrap="wrap" gap="small">
      {target.condition && (
        <Flex gap="small" style={{ maxWidth: '600px' }}>
          <LabelWrapper>{t('condition.label')}</LabelWrapper>
          <TextEllipsis tooltip={target.condition || ''}>
            <AlgorithmCondition
              condition={target.condition || ''}
              mappings={mappings}
            />
          </TextEllipsis>
        </Flex>
      )}
      {target.targetKey && (
        <Flex gap="small" style={{ maxWidth: '600px' }}>
          <LabelWrapper>{t('targetKey.label')}</LabelWrapper>
          <TextEllipsis>
            {`${requiredKeysByKeys.get(target.targetKey)?.sequenceNumber} (${
              target.targetKey
            })`}
          </TextEllipsis>
        </Flex>
      )}
      {target.description && (
        <Flex gap="small" style={{ maxWidth: '600px' }}>
          <LabelWrapper>{t('description.label')}</LabelWrapper>
          <TextEllipsis>{target.description}</TextEllipsis>
        </Flex>
      )}
      {children}
    </Flex>
  );
};

const LabelWrapper = styled(Flex)`
  text-transform: uppercase;
  font-weight: ${fontWeightStrong};
  margin: 0 4px;
  padding: 0 4px;
  background-color: ${colorGray[1]};
  border: 1px solid ${colorGray[2]};
  border-radius: 4px;
`;
